import React, { useState, useEffect } from 'react';
import { fetchPassPolicy, savePassPolicy } from './passPolicyService';
import './PassPolicyModal.css';

const DEFAULT = {
  minLength: 6,
  maxLength: 32,
  expirationTime: 0,
  requireSpecialChar: false,
  requireUppercase: false,
  requireLowercase: false,
  requireNumber: false,
  changePassFirst: false,
};

function PassPolicyModal({ open, onClose }) {
  const [data, setData] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    setMessage('');
    setLoading(true);
    fetchPassPolicy()
      .then((d) => setData({
        minLength: d.minLength ?? DEFAULT.minLength,
        maxLength: d.maxLength ?? DEFAULT.maxLength,
        expirationTime: d.expirationTime != null ? d.expirationTime : DEFAULT.expirationTime,
        requireSpecialChar: !!d.requireSpecialChar,
        requireUppercase: !!d.requireUppercase,
        requireLowercase: !!d.requireLowercase,
        requireNumber: !!d.requireNumber,
        changePassFirst: !!d.changePassFirst,
      }))
      .catch(() => setMessage('Erro ao carregar configuração.'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleNum = (key, value) => {
    const n = value === '' ? '' : parseInt(value, 10);
    if (n !== '' && isNaN(n)) return;
    setData((prev) => ({ ...prev, [key]: n }));
  };

  const handleSave = async () => {
    const min = Math.max(4, Number(data.minLength) || 4);
    const max = Math.min(32, Number(data.maxLength) || 32);
    const expirationTime = Number(data.expirationTime);
    if (min > max) {
      setMessage('Tamanho mínimo não pode ser maior que o máximo.');
      return;
    }
    if (isNaN(expirationTime) || expirationTime < 0) {
      setMessage('Tempo de expiração é obrigatório e deve ser um número não negativo.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      await savePassPolicy({
        minLength: min,
        maxLength: max,
        expirationTime,
        requireSpecialChar: data.requireSpecialChar,
        requireUppercase: data.requireUppercase,
        requireLowercase: data.requireLowercase,
        requireNumber: data.requireNumber,
        changePassFirst: data.changePassFirst,
      });
      setMessage('Salvo com sucesso.');
      setTimeout(() => { setMessage(''); onClose(); }, 800);
    } catch (e) {
      setMessage(e.message || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="pass-policy-overlay" role="dialog" aria-modal="true" aria-labelledby="pass-policy-title">
      <div className="pass-policy-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pass-policy-header">
          <h3 id="pass-policy-title" className="pass-policy-title">Configuração de política de senha</h3>
          <button type="button" className="pass-policy-close" onClick={onClose} title="Fechar">✕</button>
        </div>
        <div className="pass-policy-body">
          {loading ? (
            <p className="pass-policy-loading">Carregando...</p>
          ) : (
            <>
              <div className="pass-policy-row pass-policy-row-inline">
                <div className="pass-policy-field">
                  <label className="pass-policy-label">Tamanho mínimo de senha</label>
                  <input
                    type="number"
                    min={4}
                    max={32}
                    value={data.minLength === '' ? '' : data.minLength}
                    onChange={(e) => handleNum('minLength', e.target.value)}
                    className="pass-policy-input"
                  />
                </div>
                <div className="pass-policy-field">
                  <label className="pass-policy-label">Tempo de expiração</label>
                  <input
                    type="number"
                    min={0}
                    value={data.expirationTime === '' ? '' : data.expirationTime}
                    onChange={(e) => handleNum('expirationTime', e.target.value)}
                    className="pass-policy-input"
                    required
                  />
                </div>
              </div>
              <div className="pass-policy-row">
                <label className="pass-policy-label">Tamanho máximo de senha</label>
                <input
                  type="number"
                  min={4}
                  max={32}
                  value={data.maxLength === '' ? '' : data.maxLength}
                  onChange={(e) => handleNum('maxLength', e.target.value)}
                  className="pass-policy-input"
                />
              </div>
              <div className="pass-policy-check">
                <label className="condicoes-install-toggle">
                  <input
                    type="checkbox"
                    checked={data.requireSpecialChar}
                    onChange={(e) => setData((p) => ({ ...p, requireSpecialChar: e.target.checked }))}
                  />
                  <span className="condicoes-install-pill"><span className="condicoes-install-dot" /></span>
                </label>
                <span className="pass-policy-check-label">Obrigatoriedade de caracteres especiais</span>
              </div>
              <div className="pass-policy-check">
                <label className="condicoes-install-toggle">
                  <input
                    type="checkbox"
                    checked={data.requireUppercase}
                    onChange={(e) => setData((p) => ({ ...p, requireUppercase: e.target.checked }))}
                  />
                  <span className="condicoes-install-pill"><span className="condicoes-install-dot" /></span>
                </label>
                <span className="pass-policy-check-label">Obrigatoriedade de letras maiúsculas</span>
              </div>
              <div className="pass-policy-check">
                <label className="condicoes-install-toggle">
                  <input
                    type="checkbox"
                    checked={data.requireLowercase}
                    onChange={(e) => setData((p) => ({ ...p, requireLowercase: e.target.checked }))}
                  />
                  <span className="condicoes-install-pill"><span className="condicoes-install-dot" /></span>
                </label>
                <span className="pass-policy-check-label">Obrigatoriedade de letras minúsculas</span>
              </div>
              <div className="pass-policy-check">
                <label className="condicoes-install-toggle">
                  <input
                    type="checkbox"
                    checked={data.requireNumber}
                    onChange={(e) => setData((p) => ({ ...p, requireNumber: e.target.checked }))}
                  />
                  <span className="condicoes-install-pill"><span className="condicoes-install-dot" /></span>
                </label>
                <span className="pass-policy-check-label">Obrigatoriedade de números</span>
              </div>
              <div className="pass-policy-check">
                <label className="condicoes-install-toggle">
                  <input
                    type="checkbox"
                    checked={data.changePassFirst}
                    onChange={(e) => setData((p) => ({ ...p, changePassFirst: e.target.checked }))}
                  />
                  <span className="condicoes-install-pill"><span className="condicoes-install-dot" /></span>
                </label>
                <span className="pass-policy-check-label">Alterar senha após o primeiro login</span>
              </div>
            </>
          )}
        </div>
        {message && <p className={`pass-policy-msg ${message.includes('sucesso') ? 'pass-policy-msg-ok' : ''}`}>{message}</p>}
        <div className="pass-policy-footer">
          <button type="button" className="user-form-link-btn user-form-footer-btn" onClick={onClose}>Sair</button>
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn"
            onClick={handleSave}
            disabled={loading || saving}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PassPolicyModal;
